import { useState } from 'react'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { Dropdown, Menu } from '@pepper/ui'

import VolumePill from 'components/idea-lab/VolumePill'
import { useKeywordThemes } from 'container/SEOTools/hooks/useKeywordThemes'
import { KeywordThemes } from 'container/SEOTools/types'

import s from './ThemesDropdown.module.css'

type Props = {
  companyId: string
  domainId: string
  isContentIdeasPage: boolean
}

export const ThemesDropdown = ({
  companyId,
  domainId,
  isContentIdeasPage,
}: Props) => {
  const [isVisible, setIsVisible] = useState(false)

  const [categoryId] = useQueryParam(
    'category_id',
    withDefault(StringParam, undefined)
  )
  const [themeId, setThemeId] = useQueryParam(
    'theme_id',
    withDefault(StringParam, undefined)
  )

  const { themes } = useKeywordThemes({
    companyId,
    domainId,
    categoryId,
    enabled: !!companyId && !!domainId && !!categoryId,
    isStale: true,
  })

  const [selectedTheme, setSelectedTheme] = useState<KeywordThemes>()

  const initialSelectedTheme = themes?.find(theme => theme.id === themeId)

  const activeTheme = selectedTheme ?? initialSelectedTheme

  const filteredThemes = themes?.filter(theme => theme.id !== activeTheme?.id)

  const handleSelectTheme = (theme: KeywordThemes) => {
    setSelectedTheme(theme)
    setThemeId(theme.id)
    setIsVisible(false)
  }

  const menu = (
    <Menu>
      <div className={s.optionsWrapper}>
        <div className={s.header}>Content themes ({themes?.length})</div>
        <div className={s.options}>
          {filteredThemes?.map(themeItem => (
            <div
              key={themeItem.id}
              className={s.option}
              role="none"
              onClick={() => handleSelectTheme(themeItem)}
            >
              <div title={themeItem.theme} className={s.optionValue}>
                {themeItem.theme}
              </div>
              <VolumePill value={themeItem.volume} />
            </div>
          ))}
        </div>
      </div>
    </Menu>
  )

  return (
    <Dropdown
      overlayClassName={s.menuWrapper}
      overlay={menu}
      trigger={['click']}
      onVisibleChange={setIsVisible}
      visible={isVisible}
    >
      <div
        role="none"
        className={clsx(s.trigger, {
          [s.active]: isVisible,
        })}
      >
        <div className={s.theme}>
          {activeTheme && (
            <div
              title={activeTheme.theme}
              className={clsx(s.themeName, {
                [s.collapsedName]: !isContentIdeasPage,
              })}
            >
              {activeTheme.theme}
            </div>
          )}
          {activeTheme && <VolumePill value={activeTheme.volume} />}
        </div>
        <div className={s.icon}>
          <Icon icon="lucide:chevron-down" width={16} />
        </div>
      </div>
    </Dropdown>
  )
}
